import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import { styled } from 'styled-components';
import { Box } from '@design-system/box';
import { CATEGORIES, FontAwesomeIcon } from '@design-system/font-awesome-icon';
import { useEmojiPickerContext } from '../context/EmojiPickerContext';
const EMOJI_ICON_SIZE = 24;
const EMOJI_PICKER_WIDTH = 320;
const EMOJI_GROUP_LIST_PADDING = 12;
export const EmojiGroupList = () => {
    const { emojiData, emojiGroupWrapperRefs, focusedEmoji, selectedEmojiGroup, setSelectedEmojiGroup, } = useEmojiPickerContext();
    const emojiGroupIconRefs = useRef({});
    const emojiGroups = Array.from(Object.keys(emojiData));
    const selectedEmojiGroupPosition = emojiGroups.indexOf(selectedEmojiGroup);
    const addEmojiGroupIconRef = useCallback((groupName, ref) => {
        emojiGroupIconRefs.current[groupName] = ref;
    }, [emojiGroupIconRefs]);
    const spring = useSpring({
        x: selectedEmojiGroupPosition,
    });
    const handleEmojiGroupListFocus = useCallback(() => {
        var _a, _b;
        (_b = (_a = emojiGroupIconRefs === null || emojiGroupIconRefs === void 0 ? void 0 : emojiGroupIconRefs.current) === null || _a === void 0 ? void 0 : _a[selectedEmojiGroup]) === null || _b === void 0 ? void 0 : _b.focus();
    }, [emojiGroupIconRefs, selectedEmojiGroup]);
    const handleEmojiGroupClick = useCallback((group) => {
        var _a, _b, _c, _d;
        setSelectedEmojiGroup(group);
        (_b = (_a = emojiGroupWrapperRefs[group]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
        });
        ((_d = (_c = emojiGroupWrapperRefs[group]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.firstChild).focus();
    }, [emojiGroupWrapperRefs, setSelectedEmojiGroup]);
    const handleEmojiGroupKeyDown = useCallback((e, group, index) => {
        var _a, _b, _c, _d;
        if (e.key === 'ArrowLeft' || (e.shiftKey && e.key === 'Tab')) {
            e.preventDefault();
            const indexAt = index - 1 < 0 ? emojiGroups.length - 1 : index - 1;
            (_b = (_a = emojiGroupIconRefs === null || emojiGroupIconRefs === void 0 ? void 0 : emojiGroupIconRefs.current) === null || _a === void 0 ? void 0 : _a[emojiGroups[indexAt]]) === null || _b === void 0 ? void 0 : _b.focus();
        }
        if (e.key === 'ArrowRight' || (!e.shiftKey && e.key === 'Tab')) {
            e.preventDefault();
            const indexAt = emojiGroups.length <= index + 1 ? 0 : index + 1;
            (_d = (_c = emojiGroupIconRefs === null || emojiGroupIconRefs === void 0 ? void 0 : emojiGroupIconRefs.current) === null || _c === void 0 ? void 0 : _c[emojiGroups[indexAt]]) === null || _d === void 0 ? void 0 : _d.focus();
        }
        if (e.key === 'Enter') {
            handleEmojiGroupClick(group);
        }
    }, [emojiGroups, handleEmojiGroupClick]);
    const handleEmojiGroupKeyUp = useCallback((e) => {
        var _a, _b, _c;
        if (e.key === 'ArrowDown') {
            if (focusedEmoji) {
                (_a = focusedEmoji === null || focusedEmoji === void 0 ? void 0 : focusedEmoji.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            else {
                ((_c = (_b = emojiGroupWrapperRefs[selectedEmojiGroup]) === null || _b === void 0 ? void 0 : _b.current) === null || _c === void 0 ? void 0 : _c.firstChild).focus();
            }
        }
    }, [emojiGroupWrapperRefs, focusedEmoji, selectedEmojiGroup]);
    const handleEmojiGroupFocus = (e) => {
        e.stopPropagation();
    };
    const setEmojiGroupIconRef = (group, element) => {
        addEmojiGroupIconRef(group, element);
    };
    const emojiSpacing = (EMOJI_PICKER_WIDTH -
        EMOJI_GROUP_LIST_PADDING * 2 -
        EMOJI_ICON_SIZE * emojiGroups.length) /
        (emojiGroups.length - 1);
    return (_jsxs(Wrapper, { flexDirection: "column", onFocus: handleEmojiGroupListFocus, pt: 4, px: `${EMOJI_GROUP_LIST_PADDING}px`, tabIndex: 0, children: [_jsx(Box, { justifyContent: "space-between", children: emojiGroups.map((group, index) => (_jsx(EmojiGroupIcon, { mr: 3, onClick: () => handleEmojiGroupClick(group), onFocus: handleEmojiGroupFocus, onKeyDown: (event) => handleEmojiGroupKeyDown(event, group, index), onKeyUp: handleEmojiGroupKeyUp, ref: (element) => setEmojiGroupIconRef(group, element), tabIndex: 0, title: group, children: _jsx(GroupIcon, { group: group }) }, group))) }), _jsx(Box, { height: "2px", mt: 1, children: _jsx(animated.div, { style: {
                        transform: spring.x.to((x) => `translateX(calc(${x * 100}% + ${x * emojiSpacing}px))`),
                    }, children: _jsx(Underline, {}) }) }), _jsx(EmojiGroupTitle, { mb: 2, mt: 4, children: selectedEmojiGroup })] }));
};
const GroupIcon = ({ group }) => {
    if (group === 'Smileys & Emotion') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "face-grin", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Animals & Nature') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "squirrel", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Food & Drink') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "soft-serve", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Travel & Places') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "car-rear", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Activities') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "futbol", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Objects') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "lightbulb", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Symbols') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "peace", iconType: CATEGORIES.LIGHT }));
    }
    if (group === 'Flags') {
        return (_jsx(FontAwesomeIcon, { height: "20px", iconName: "flag", iconType: CATEGORIES.LIGHT }));
    }
    return null;
};
const EmojiGroupIcon = styled(Box).attrs(() => ({
    height: EMOJI_ICON_SIZE,
    width: EMOJI_ICON_SIZE,
})) `
  cursor: pointer;
  justify-content: center;
  align-items: center;

  &:last-of-type {
    margin-right: 0;
  }
`;
const EmojiGroupTitle = styled(Box).attrs(() => ({
    font: 'metadata-semibold-asphalt',
})) ``;
const Wrapper = styled(Box) `
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
`;
const Underline = styled(Box).attrs(() => ({
    bg: 'primary',
    height: '100%',
    width: EMOJI_ICON_SIZE,
})) `
  border-radius: 4px;
`;
//# sourceMappingURL=EmojiGroupList.js.map